import { useEffect, useState, useCallback } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import { ExpertCard } from "../components/ExpertCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useStartChat } from "../hooks/useStartChat";

const SPECIALTIES = ["Tarot", "Médiumnité", "Astrologie", "Cartomancie", "Numérologie", "Amour", "Travail", "Guidance", "Spiritualité", "Bien-être", "Deuil"];
const LANGUAGES = ["Français", "Anglais", "Espagnol", "Italien"];

export default function Directory() {
  const [experts, setExperts] = useState([]);
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("all");
  const [language, setLanguage] = useState("all");
  const [sort, setSort] = useState("popular");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const startChat = useStartChat();

  const load = useCallback(async () => {
    setLoading(true);
    const params = { sort };
    if (q) params.q = q;
    if (specialty !== "all") params.specialty = specialty;
    if (language !== "all") params.language = language;
    if (availableOnly) params.availability = true;
    try {
      const { data } = await api.listExperts(params);
      setExperts(data);
    } finally { setLoading(false); }
  }, [q, specialty, language, sort, availableOnly]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <h1 className="font-serif text-3xl text-celeste-ivoire sm:text-4xl">Annuaire des praticiens</h1>
      <p className="mt-2 text-sm text-celeste-lilas/75">Trouvez la guidance qui vous correspond grâce aux filtres.</p>

      <div className="glass mt-6 rounded-3xl p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-celeste-lilas/60" />
            <Input data-testid="directory-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un nom, une spécialité…" className="bg-celeste-night/50 border-celeste-violet/30 pl-9 text-celeste-ivoire" />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:flex">
            <Select value={specialty} onValueChange={setSpecialty}>
              <SelectTrigger data-testid="filter-specialty" className="bg-celeste-night/50 border-celeste-violet/30 text-celeste-lilas min-w-[130px]"><SelectValue placeholder="Spécialité" /></SelectTrigger>
              <SelectContent className="glass-strong text-celeste-ivoire">
                <SelectItem value="all">Toutes spécialités</SelectItem>
                {SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="filter-language" className="bg-celeste-night/50 border-celeste-violet/30 text-celeste-lilas min-w-[110px]"><SelectValue placeholder="Langue" /></SelectTrigger>
              <SelectContent className="glass-strong text-celeste-ivoire">
                <SelectItem value="all">Toutes langues</SelectItem>
                {LANGUAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="filter-sort" className="bg-celeste-night/50 border-celeste-violet/30 text-celeste-lilas min-w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong text-celeste-ivoire">
                <SelectItem value="popular">Populaires</SelectItem>
                <SelectItem value="rating">Mieux notés</SelectItem>
                <SelectItem value="price_asc">Prix croissant</SelectItem>
                <SelectItem value="price_desc">Prix décroissant</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Switch data-testid="filter-availability" checked={availableOnly} onCheckedChange={setAvailableOnly} className="data-[state=checked]:bg-celeste-or" />
          <span className="text-sm text-celeste-lilas/80"><SlidersHorizontal className="mr-1 inline h-3.5 w-3.5" /> Disponibles immédiatement</span>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-celeste-lilas/70">Chargement des praticiens…</div>
      ) : experts.length === 0 ? (
        <div className="py-20 text-center text-celeste-lilas/70" data-testid="no-experts">Aucun praticien ne correspond à ces critères.</div>
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="experts-grid">
          {experts.map((e) => <ExpertCard key={e.id} expert={e} onStart={startChat} />)}
        </div>
      )}
    </div>
  );
}
